import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import { MarketingNav } from "./MarketingNav";
import { MarketingFooter } from "./MarketingFooter";
import { Seo } from "./Seo";
import { FaqSection } from "./Faq";
import { Reveal } from "./anim";
import { AerospaceBanner } from "./FleetIndex";
import { SERVICES, DEFENCE_SERVICE, CONTACT, IMAGES, SINCE, breadcrumb, faqSchema, SITE } from "./marketingData";

const ALL = [...SERVICES, DEFENCE_SERVICE];

const INDEX_FAQ = [
  { q: "What logistics services does ITO offer?", a: "Indian Transport Organisation offers full truck load (FTL), part load & parcel (LTL), warehousing & handling, air & express cargo, project/heavy/ODC cargo, a pan-India network, and defence & government logistics — all under one accountable partner since 1972." },
  { q: "Do you cover all of India?", a: "Yes. We pick up and deliver to and from every pincode in India by road and air, on a single Lorry Receipt, including Leh, Ladakh and border regions." },
  { q: "How do I get a quote for a service?", a: `Call ${CONTACT.phone} or email ${CONTACT.email} with your cargo type, weight, dimensions, pickup and delivery locations, and our team will quote quickly.` },
];

export default function ServicesIndex() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const jsonLd = [
    breadcrumb([{ name: "Home", path: "/" }, { name: "Services", path: "/services" }]),
    {
      "@context": "https://schema.org", "@type": "ItemList",
      itemListElement: ALL.map((s, i) => ({
        "@type": "ListItem", position: i + 1, name: s.title,
        url: `${SITE}/services/${s.slug}`,
      })),
    },
    faqSchema(INDEX_FAQ),
  ];

  return (
    <div className="bg-[#F9F8F6] text-[#1A1A18] font-body antialiased overflow-x-hidden" data-testid="services-index">
      <Seo
        title={`Logistics Services in India — FTL, ODC, Air, Defence & More | ITO (since ${SINCE})`}
        description="ITO's logistics services across India: full truck load, part load & parcel, warehousing, air & express cargo, project & ODC cargo, pan-India network and defence logistics. One accountable partner to every pincode."
        path="/services"
        image={IMAGES.odcTransformer}
        imageAlt="ITO logistics services across India"
        keywords="logistics services india, transport company india, ftl transporter, odc transport, air cargo india, warehousing india, defence logistics, pan india transporter"
        jsonLd={jsonLd}
      />
      <MarketingNav heroDark />

      <section className="relative min-h-[46vh] flex items-end pb-12 pt-32 overflow-hidden">
        <img src={IMAGES.odcTransformer} alt="ITO logistics services across India" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A18]/93 via-[#1A1A18]/60 to-[#1A1A18]/30" />
        <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 w-full">
          <p className="text-xs font-semibold tracking-widest uppercase text-[#D4A373] mb-3">Our services</p>
          <h1 className="font-heading font-black tracking-tight text-white text-3xl sm:text-5xl leading-[1.05] max-w-3xl">
            Logistics services for every kind of cargo
          </h1>
          <p className="mt-4 text-lg text-white/85 max-w-2xl leading-relaxed">
            From full truck loads to over-dimensional project cargo, air freight and defence movements — one accountable partner to every pincode in India, since {SINCE}.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {ALL.map((s, i) => (
              <Reveal key={s.slug} delay={(i % 3) * 0.06}>
                <Link to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`}
                  className="group block h-full rounded-2xl overflow-hidden border border-[#1A1A18]/10 bg-white hover:border-[#C84B31]/50 hover:shadow-lg transition-all hover:-translate-y-1">
                  <div className="relative h-40 overflow-hidden">
                    <img src={s.image} alt={s.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                    <span className="absolute top-3 left-3 text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded bg-[#1A1A18]/80 text-white">{s.tag}</span>
                  </div>
                  <div className="p-5">
                    <h2 className="font-heading font-semibold text-lg mb-1.5">{s.title}</h2>
                    <p className="text-sm text-[#4F4C44] leading-relaxed mb-3 line-clamp-3">{s.short}</p>
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#C84B31]">
                      View service <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>

          <AerospaceBanner id="services" />

          <Reveal className="mt-12">
            <div className="rounded-2xl bg-[#1A1A18] text-white p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
              <div>
                <h2 className="font-heading font-bold text-2xl mb-1">Not sure which service you need?</h2>
                <p className="text-white/80 text-sm">Tell us your cargo and route — we'll recommend the right one.</p>
              </div>
              <a href={CONTACT.phoneHref} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C84B31] px-6 py-3 text-sm font-semibold text-white hover:bg-[#a83c26] transition-colors">
                <Phone className="w-4 h-4" /> Call {CONTACT.phone}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <FaqSection faqs={INDEX_FAQ} title="Services — frequently asked questions" testid="services-index-faq" />

      <MarketingFooter />
    </div>
  );
}
